import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { DEFAULT_SETTINGS, normalizeSettings } from '../constants/settings'
import { getSettings, updateSettings } from '../service'

// The signed-in user's settings for every page: the defaults at once, then the saved values.
// A change shows immediately and is saved in the background; a refused save puts it back.
const SettingsContext = createContext({
  settings: DEFAULT_SETTINGS,
  loaded: false,
  error: null,
  updateSetting: async () => {}
})

export function SettingsProvider ({ children }) {
  const [settings, setSettings] = useState(DEFAULT_SETTINGS)
  const [loaded, setLoaded] = useState(false)
  const [error, setError] = useState(null)
  const current = useRef(DEFAULT_SETTINGS)
  const apply = next => { current.current = next; setSettings(next) }

  useEffect(() => {
    let cancelled = false
    getSettings()
      .then(body => { if (!cancelled) apply(normalizeSettings(body?.settings)) })
      .catch(err => {
        // Without saved settings every page still works on the defaults.
        console.warn('Unable to load settings; using the defaults.', err)
      })
      .finally(() => { if (!cancelled) setLoaded(true) })
    return () => { cancelled = true }
  }, [])

  const updateSetting = useCallback(async (key, value) => {
    const previous = current.current
    apply(normalizeSettings({ ...previous, [key]: value }))
    setError(null)
    try {
      const body = await updateSettings({ [key]: value })
      apply(normalizeSettings(body?.settings))
    } catch (err) {
      apply(previous)
      setError(err)
      throw err
    }
  }, [])

  const value = useMemo(() => ({ settings, loaded, error, updateSetting }), [settings, loaded, error, updateSetting])
  return <SettingsContext.Provider value={value}>{children}</SettingsContext.Provider>
}

SettingsProvider.propTypes = { children: PropTypes.node }

export const useSettings = () => useContext(SettingsContext)
export const useSetting = key => useContext(SettingsContext).settings[key]
